/**
 * Panel state for Work-in-session: which Work project backs the current
 * session's Work panel (``EvidenceSidebarPanel`` / ``ResizableMenuPanels``,
 * gated on ``useIsWorkInSessionEnabled``).
 *
 * Mirrors the Evidence pair (``sidebarChatIdAtomFamily`` +
 * ``activeSidebarChatIdAtom``). ``GET /projects`` now supports a
 * ``session_id`` filter, which powers ``SessionWorkProjectSelector`` (the
 * manual switcher — pick a different project this session already has) via
 * ``useSelectWorkPanelProject``. A future ``useAutoSelectRecentSessionProject``
 * could additionally seed {@link workPanelProjectIdAtomFamily} on mount
 * (recovering the last-open project after a hard reload without a manual
 * pick) without the panels or this file changing shape.
 */

import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type { ApprovalMode, AttachmentBlock } from '../../contracts/harness.types';
import type { ProjectRunFlags } from '../../contracts/project-run-flags';
import type { RpaSegmentedTask } from '../../contracts/projects.types';
import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { WorkSurface } from '../../contracts/work-home-composer-analytics.types';
import type { ProjectsComposerPromptDraft } from './projects-composer-editor-draft';

/**
 * Per-session memory of the Work project shown in that session's panel.
 * Scoped per session (like ``sidebarChatIdAtomFamily``) so switching
 * sessions can never leak one session's project into another's panel.
 *
 * In-memory only: written when the floating bar / chips create a project
 * for a session (``useCreateSessionWorkProject``) and when the user manually
 * switches projects via the header selector (``useSelectWorkPanelProject``).
 * Either way this resumes the same tab's closed-and-reopened panel; a hard
 * reload still forgets it — ``SessionWorkProjectSelector`` (backed by the
 * ``session_id`` filter on ``GET /projects``) is today's recovery path for
 * that case, until a real auto-select-on-mount hook seeds this from the
 * backend instead.
 */
export const workPanelProjectIdAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom<string | null>(null)
);

/**
 * "Show this project right now" override, mirroring ``activeSidebarChatIdAtom``.
 * Set the instant a new project is created so the panel renders it
 * immediately without waiting on a separate read of the per-session atom
 * above. Kept as its own slot (rather than folded into the family write)
 * so a future auto-select hook can resolve independently without racing
 * a fresh creation.
 *
 * Carries the ``sessionId`` it was created for so {@link useWorkPanelProjectId}
 * can ignore it once the session changes — a plain global value would leak
 * across sessions the moment ``SessionWorkPanel`` unmounts (closing the panel
 * drops any ref-based "clear on session change" tracking with it), and this
 * feature's whole premise is that Work content never crosses a session
 * boundary.
 */
export const activeWorkPanelProjectIdAtom = atom<{
  sessionId: string;
  projectId: string;
  /** Keep an explicitly selected out-of-scope project mounted read-only. */
  inspectReadOnly?: boolean;
} | null>(null);

/**
 * The opening turn the floating bar handed off for a session, waiting for
 * that session's ``SessionWorkPanel`` to consume it.
 *
 * The bar can't create the project itself: sending opens the panel, which
 * makes ``SessionAskHeidiContainer`` return ``null`` and unmounts the bar —
 * tearing down the create mutation before its ``onSuccess`` lands (empty
 * panel; a retry then spawns duplicate server-side projects). So the bar
 * only STASHES the intent here and opens the panel; the panel — which stays
 * mounted while open — reads this, runs the create, and clears it.
 *
 * Self-scoped with the ``sessionId`` it was stashed for (like
 * {@link activeWorkPanelProjectIdAtom}) so a stale intent can never be
 * consumed by a different session's panel after a session switch.
 */
export interface PendingWorkFirstTurn {
  sessionId: string;
  prompt: string;
  linked: LinkedContextItem | null;
  /** User-visible text for the optimistic bubble when it differs from ``prompt``. */
  displayPrompt?: string;
  files?: File[];
  attachment_ids?: string[];
  optimisticAttachments?: AttachmentBlock[];
  runFlags?: ProjectRunFlags;
  computerUseSkill?: RpaSegmentedTask;
  approvalMode?: ApprovalMode;
  analytics?: {
    surface: WorkSurface;
    work_mode?: string;
    has_slash_command?: boolean;
    entry_point?: string;
    clinical_session_id?: string;
  };
  /**
   * Text the clinician had typed when they sent a shortcut from the floating
   * bar. The bar unmounts as the panel opens; the panel composer starts here.
   */
  composerDraft?: ProjectsComposerPromptDraft;
}

export const pendingWorkFirstTurnAtom = atom<PendingWorkFirstTurn | null>(null);
